// src/features/ownerAdmin/CreatorOverviewPage.tsx
import {
  useCreatorStats, type CommandStatRow,
  useFeatureFlags, useToggleFeatureFlag,
} from './useCreatorData';
import { formatRelative } from '../../lib/formatTime';
import './OwnerAdminPage.css';

function CommandStatsTable({ rows, title }: { rows: CommandStatRow[]; title: string }) {
  return (
    <div className="owner-admin-subtable">
      <h3>{title}</h3>
      <table className="owner-admin-table">
        <thead><tr><th>Command</th><th>Uses</th><th>Failures</th><th>Avg ms</th></tr></thead>
        <tbody>
          {rows.map((r, index) => (
            <tr key={r.command} className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
              <td className="owner-admin-table__mono">/{r.command}</td>
              <td>{Number(r.total).toLocaleString()}</td>
              <td style={{ color: Number(r.failures) > 0 ? 'var(--negative)' : undefined }}>{r.failures}</td>
              <td>{r.avg_ms ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GlobalStatsSection() {
  const { data: stats, isLoading, error } = useCreatorStats();

  if (isLoading) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>;
  if (error) return <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load stats.</p>;
  if (!stats) return null;

  return (
    <>
      <div className="stat-grid">
        <div className="stat-card">
          <p className="stat-card__label">Bot commands, total</p>
          <p className="stat-card__value">{stats.bot.total.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Website requests, total</p>
          <p className="stat-card__value">{stats.website.total.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Distinct error types</p>
          <p className="stat-card__value" style={{ color: stats.errors.length > 0 ? 'var(--negative)' : undefined }}>{stats.errors.length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Unresolved reports</p>
          <p className="stat-card__value gold">
            {stats.reports.find((r) => r.status === 'unresolved')?.total ?? 0}
          </p>
        </div>
      </div>

      <div className="owner-admin-grid">
        <CommandStatsTable rows={stats.bot.topCmds} title="Top bot commands" />
        <CommandStatsTable rows={stats.website.topRoutes} title="Top website routes" />
      </div>

      {stats.errors.length > 0 && (
        <div className="owner-admin-subtable">
          <h3>Most errored commands</h3>
          <table className="owner-admin-table">
            <thead><tr><th>Command</th><th>Source</th><th>Errors</th></tr></thead>
            <tbody>
              {stats.errors.map((e, index) => (
                <tr key={`${e.source}-${e.command}`} className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
                  <td className="owner-admin-table__mono">/{e.command}</td>
                  <td>{e.source}</td>
                  <td style={{ color: 'var(--negative)' }}>{e.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function FeatureFlagsSection() {
  const { data: flags, isLoading, error } = useFeatureFlags();
  const toggle = useToggleFeatureFlag();

  if (isLoading) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>;
  if (error) return <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load feature flags.</p>;
  if (!flags?.length) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No feature flags defined.</p>;

  return (
    <table className="owner-admin-table">
      <thead><tr><th>Feature</th><th>Status</th><th>Last changed</th><th></th></tr></thead>
      <tbody>
        {flags.map((flag, index) => (
          <tr key={flag.flag_name} className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
            <td className="owner-admin-table__mono">{flag.flag_name.replace(/_/g, ' ')}</td>
            <td>
              {flag.enabled
                ? <span className="status-badge status-badge--fresh">Enabled</span>
                : <span className="status-badge status-badge--error">Disabled</span>}
            </td>
            <td className="owner-admin-table__mono" style={{ fontSize: 11 }}>{formatRelative(flag.updated_at)}</td>
            <td>
              <button
                className={flag.enabled ? 'button-danger' : 'button-primary'}
                onClick={() => toggle.mutate({ flagName: flag.flag_name, enabled: !flag.enabled })}
                disabled={toggle.isPending}
              >
                {flag.enabled ? 'Disable' : 'Enable'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CreatorOverviewPage() {
  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Bot owner only</p>
        <h1 className="page-header__title">Owner Overview</h1>
      </div>

      <article className="card" style={{ marginBottom: '1.25rem' }}>
        <header className="card__header"><h2 className="card__title">Global usage stats</h2></header>
        <div className="card__content"><GlobalStatsSection /></div>
      </article>

      <article className="card">
        <header className="card__header">
          <div>
            <p className="card__eyebrow">Kill-switches</p>
            <h2 className="card__title">Feature Flags</h2>
          </div>
        </header>
        <div className="card__content"><FeatureFlagsSection /></div>
      </article>
    </>
  );
}
